import { AddonOutput, OrderOutput, OrderedAddonOutput } from 'dtos/shared/addon';
import { makeAutoObservable, runInAction } from 'mobx';
import AmplitudeService from 'services/amplitude.service';
import { AuthStore } from 'stores/users/AuthStore';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { LoadStatus } from 'utils/types';

export type AvailableAddon = {
  title: string;
  description: string;
  price: number;
  _id: string;
  isSelected: boolean;
};

export type OrderedAddon = {
  _id: string;
  title: string;
  description: string;
  price: number;
};

export class AddonStore {
  isOpen: boolean = false;
  orderSubmitted: boolean = false;
  loadStatus: LoadStatus = 'loading';

  availableAddons: AvailableAddon[] = [];
  orderedAddons: OrderedAddon[] = [];

  constructor(private readonly token: string, private readonly authStore: AuthStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async activate() {
    await this.getAvailableAndOrderedAddons();
  }

  private async getAvailableAndOrderedAddons() {
    const allAddons = await this.getAllAddons();
    const orders = await this.getOrdersByJobToken();

    const uniqueOrderedAddonIds = this.getAllUniqueAddonIds(orders);

    const availableAddons = allAddons
      .map((addon) => {
        return {
          _id: addon.id,
          title: addon.title,
          description: addon.description,
          price: addon.price,
          isSelected: false,
        };
      })
      .filter((addon) => {
        return !uniqueOrderedAddonIds.includes(addon._id);
      });

    runInAction(() => {
      this.availableAddons = availableAddons;
      this.orderedAddons = orders.flatMap((order) => order.addonsOrdered);
      this.loadStatus = 'success';
    });
  }

  toggleAddonSelection(addonId: string, jobDetails?: Record<string, any>) {
    this.availableAddons = this.availableAddons.map((addon) => {
      if (addon._id === addonId) {
        const updatedAddon = {
          ...addon,
          isSelected: !addon.isSelected,
        };

        AmplitudeService.trackEvent(updatedAddon.isSelected ? 'Add Add-On' : 'Remove Add-On', {
          addon: updatedAddon,
          ...jobDetails,
        });

        return updatedAddon;
      }

      return addon;
    });
  }

  private getAllUniqueAddonIds(orders: OrderOutput[]) {
    const uniqueAddonIdsSet = new Set();

    orders.forEach((order) => {
      order.addonsOrdered.forEach((addon) => {
        uniqueAddonIdsSet.add(addon._id);
      });
    });

    const uniqueAddonIdsArray = Array.from(uniqueAddonIdsSet);
    return uniqueAddonIdsArray;
  }

  openDrawer() {
    this.getAvailableAndOrderedAddons();
    this.isOpen = true;
    this.orderSubmitted = false;
  }

  closeDrawer() {
    this.isOpen = false;
    this.orderSubmitted = false;
  }

  async submitOrder(jobDetails: Record<string, any> = {}) {
    const selectedAddons = this.availableAddons.filter((addon) => addon.isSelected);

    const addonsOrdered = selectedAddons.map(({ isSelected, ...addonOrdered }) => {
      return addonOrdered;
    });

    await this.createOrder(addonsOrdered);

    AmplitudeService.trackEvent('Send Add-On Request', { addonsOrdered, ...jobDetails });
  }

  get totalCost() {
    let total = 0;

    this.availableAddons
      .filter((addon) => addon.isSelected)
      .forEach((addon) => {
        total += addon.price;
      });

    return total;
  }

  private async getAllAddons() {
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/addons`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Error getting addons');

      const output: AddonOutput[] = nullsToUndefined(await response.json());
      return output;
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  async createOrder(addonsOrdered: OrderedAddonOutput[]) {
    try {
      const order = {
        addonsOrdered,
        orderedBy: this.authStore.currentUser?.email,
        totalCost: this.totalCost,
      };

      const response = await this.authStore.fetchWithAuth(
        `${serverUri}/orders/${encodeURIComponent(this.token)}/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(order),
        }
      );
      if (!response.ok) throw new Error('Error creating order');

      runInAction(() => {
        this.orderSubmitted = true;
      });
    } catch (err) {
      console.error(err);
    }
  }

  private async getOrdersByJobToken() {
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/orders/${encodeURIComponent(this.token)}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Error getting addons');

      const output: OrderOutput[] = nullsToUndefined(await response.json());
      return output;
    } catch (err) {
      console.error(err);
      return [];
    }
  }
}
